import { v4 as uuidv4 } from 'uuid';
import {
  type AudioFlowStatus,
  type AudioIdentity,
  type BrowserAudioFlow,
  safeAudioFlow,
} from '../../../services/audio-flow';

import { arrayBufferToBase64 } from '../../../utils/array-buffer-to-base-64';
import { base64ToArrayBuffer } from '../../../utils/base-64-to-array-buffer';

export const DISCONNECT_INTENT_REASONS = [
  'user_action_quit',
  'page_hide',
  'frontend_error',
  'unknown',
] as const;

export type DisconnectIntentReason = (typeof DISCONNECT_INTENT_REASONS)[number];

export type CloseEvent = {
  type: 'close';
  code?: number;
  reason?: string;
  wasClean?: boolean;
};

export type ErrorEvent = {
  type: 'error';
  id: string;
  timestamp: number;
  error: {
    type: string;
    message: string;
    messageId: string;
  };
};

export type InterviewStartedEvent = {
  type: 'interview.started';
  id: string;
  timestamp: number;
  videoUploadUrl: string;
  screenRecordingUploadUrl?: string;
  startedAt: Date;
  duration: number | null;
  inputSampleRate: number;
  audioFlow?: AudioFlowStatus;
};

export type ResponseAudioEvent = AudioIdentity & {
  audioSequence?: number;
  type: 'response.audio';
  id: string;
  timestamp: number;
  audio: ArrayBuffer;
};

export type ResponseInterruptedEvent = {
  type: 'response.interrupted';
  id: string;
  timestamp: number;
};

export type InterviewCompleteEvent = {
  type: 'interview.complete';
  id: string;
  timestamp: number;
};

export type InterviewCompletedEvent = {
  type: 'interview.completed';
  id: string;
  timestamp: number;
};

export type InterviewStageEvent = {
  type: 'interview.stage';
  id: string;
  timestamp: number;
  stage: 'INTRO' | 'INTERVIEW' | 'WRAP';
  reason:
    | 'initial'
    | 'resume'
    | 'finish_intro_tool'
    | 'start_wrap_tool'
    | 'intro_timeout'
    | 'intro_playback_fallback'
    | 'duration_timer';
};

export type PongEvent = {
  type: 'pong';
  id: string;
  timestamp: number;
  pingId: string;
};

interface EventMap {
  close: CloseEvent;
  error: ErrorEvent;
  'interview.complete': InterviewCompleteEvent;
  'interview.completed': InterviewCompletedEvent;
  'interview.started': InterviewStartedEvent;
  'interview.stage': InterviewStageEvent;
  'response.audio': ResponseAudioEvent;
  'response.interrupted': ResponseInterruptedEvent;
  pong: PongEvent;
}

class ChatClient {
  private socket: WebSocket | null = null;
  private audioFlowNegotiated = false;

  private eventListeners: {
    [K in keyof EventMap]?: ((event: EventMap[K]) => void)[];
  } = {};

  constructor(
    private apiUrl: string,
    private audioFlow?: BrowserAudioFlow,
  ) {
    if (audioFlow) {
      audioFlow.socketState = () => ({
        readyState: this.socket?.readyState ?? 3,
        bufferedAmount: this.socket?.bufferedAmount ?? 0,
      });
      audioFlow.sendReport = (report) => {
        if (this.audioFlowNegotiated) this.sendMessage(report);
      };
    }
  }

  private dispatchEvent<K extends keyof EventMap>(
    eventType: K,
    event: EventMap[K],
  ) {
    this.eventListeners[eventType]?.forEach((listener) => {
      listener(event);
    });
  }

  // TODO: (20) validate server message shapes.
  // biome-ignore lint/suspicious/noExplicitAny: websocket payloads are loosely typed
  private handleMessage(message: any) {
    const type = message.type;
    if (typeof type !== 'string') {
      console.warn('Message type is not a string:', type);
      return;
    }

    switch (type) {
      case 'response.audio': {
        const audio = base64ToArrayBuffer(message.audio);
        safeAudioFlow(() =>
          this.audioFlow?.outputReceived(
            audio.byteLength,
            message.audioSequence,
            message,
          ),
        );
        this.dispatchEvent(type, { ...message, audio });
        break;
      }

      case 'interview.audio_flow':
        safeAudioFlow(() => {
          this.audioFlowNegotiated =
            this.audioFlow?.configure(message.audioFlow) ?? false;
        });
        break;
      case 'interview.started':
        safeAudioFlow(() => {
          if (message.audioFlow)
            this.audioFlowNegotiated =
              this.audioFlow?.configure(message.audioFlow) ?? false;
        });
        this.dispatchEvent('interview.started', {
          ...message,
          createdAt: new Date(message.createdAt),
          startedAt: message.startedAt ? new Date(message.startedAt) : null,
        });
        break;

      case 'error':
      case 'interview.complete':
      case 'interview.completed':
      case 'interview.stage':
      case 'pong': // Pong received after ping, connection is alive
      case 'response.interrupted':
        if (type === 'interview.stage' && this.audioFlow?.status)
          this.audioFlow.status.stage = message.stage;
        if (type === 'interview.completed')
          safeAudioFlow(() => this.audioFlow?.finish());
        this.dispatchEvent(type, message);
        break;

      default:
        console.warn('Unknown message type:', message.type);
    }
  }

  private sendMessage(message: object): string {
    if (!this.socket) {
      throw new Error('Client is not connected');
    }

    const id = uuidv4();
    this.socket.send(
      JSON.stringify({
        ...message,
        id,
      }),
    );

    return id;
  }

  async connect() {
    if (this.isConnected()) {
      console.log('Already connected');
      return;
    }

    this.audioFlowNegotiated = false;
    this.audioSequence = 0;
    safeAudioFlow(() => this.audioFlow?.beginConnection());
    return new Promise<void>((resolve, reject) => {
      const socket = new WebSocket(this.apiUrl);

      socket.onopen = () => {
        socket.onerror = (err) => {
          console.error('WebSocket error:', err);
        };
        this.socket = socket;
        resolve();
      };

      socket.onerror = (err) => {
        reject(err);
      };

      socket.onclose = ({ code, reason, wasClean }) => {
        this.socket = null;
        this.dispatchEvent('close', { type: 'close', code, reason, wasClean });
      };

      socket.onmessage = (event) => {
        let decoded: unknown;
        try {
          decoded = JSON.parse(event.data);
        } catch (err) {
          console.error('Failed to parse message:', event.data, err);
        }

        if (!decoded || typeof decoded !== 'object') {
          console.warn('Message is not an object:', decoded);
          return;
        }

        try {
          this.handleMessage(decoded);
        } catch (err) {
          console.error('Failed to handle message:', err);
        }
      };
    });
  }

  isConnected() {
    return this.socket !== null && this.socket.readyState === WebSocket.OPEN;
  }

  private audioSequence = 0;

  appendInputAudio(buffer: ArrayBuffer): string {
    if (this.audioFlow)
      this.audioFlow.counters.inputAttemptBytes += buffer.byteLength;
    try {
      const id = this.sendMessage({
        type: 'input_audio_buffer.append',
        audio: arrayBufferToBase64(buffer),
        ...(this.audioFlowNegotiated
          ? { audioSequence: ++this.audioSequence }
          : {}),
      });
      safeAudioFlow(() => this.audioFlow?.inputEnqueued(buffer.byteLength));
      return id;
    } catch (error) {
      if (this.audioFlow)
        this.audioFlow.counters.inputFailedBytes += buffer.byteLength;
      throw error;
    }
  }

  appendInputImage(imageDataUrl: string): string {
    // expecting data:image/jpeg;base64,xxxxxxxx or data:image/png;base64,xxxxxxxx
    if (!imageDataUrl.startsWith('data:image/')) {
      console.warn('Invalid image data URL format');
      return '';
    }

    return this.sendMessage({
      type: 'input_image.append',
      image: imageDataUrl,
    });
  }

  startInterview(
    interviewId: string,
    authToken: string,
    inviteCode: string | null,
    requestedProvider?: 'aurora' | 'polaris' | 'zenith',
  ): string {
    return this.sendMessage({
      type: 'interview.start',
      interviewId,
      authToken,
      ...(this.audioFlow
        ? {
            audioFlowVersion: 1,
            browserInstanceId: this.audioFlow.browserInstanceId,
          }
        : {}),
      ...(inviteCode ? { inviteCode } : {}),
      ...(requestedProvider && { requestedProvider }),
    });
  }

  ping(): string {
    return this.sendMessage({ type: 'ping' });
  }

  // Signals client audio queue drained; server uses this to defer stage
  // transitions until the candidate has actually finished hearing the model.
  sendPlaybackComplete(): string {
    return this.sendMessage({ type: 'client.playback_complete' });
  }

  completeInterview() {
    this.sendMessage({ type: 'interview.complete' });
  }

  on<K extends keyof EventMap>(
    eventType: K,
    listener: (event: EventMap[K]) => void,
  ) {
    if (!this.eventListeners[eventType]) {
      this.eventListeners[eventType] = [];
    }

    this.eventListeners[eventType].push(listener);
  }

  off<K extends keyof EventMap>(
    eventType: K,
    listener: (event: EventMap[K]) => void,
  ) {
    const listeners = this.eventListeners[eventType];
    if (!listeners) {
      return;
    }

    const filtered = listeners.filter((l) => l !== listener);
    this.eventListeners[eventType] = filtered as NonNullable<
      (typeof this.eventListeners)[K]
    >;
  }

  removeAllListeners() {
    this.eventListeners = {};
  }

  disconnect(reason: DisconnectIntentReason = 'unknown', detail?: string) {
    if (!this.socket) {
      return;
    }
    if (this.socket.readyState === WebSocket.OPEN) {
      try {
        this.sendMessage({ type: 'disconnect_intent', reason, detail });
      } catch (err) {
        // Best-effort signal; never block close on it.
        console.warn('Failed to send disconnect_intent before close', err);
      }
    }
    this.socket.close();
  }
}

export default ChatClient;
